import React, { useState, useEffect } from 'react';
import {
  Globe,
  Plus,
  Search,
  TrendingUp,
  FileText,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Upload,
  Edit,
  Save,
  X,
  Target,
  BarChart3,
  Lightbulb,
  Layout,
  Users as UsersIcon,
  Link as LinkIcon
} from 'lucide-react';
import { seoService } from '../../lib/seoService';

export default function SEOManager() {
  const [selectedClient, setSelectedClient] = useState('f8a6a078-1684-4fc0-a7cb-70699d62105b');
  const [connections, setConnections] = useState<any[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<any>(null);
  const [pages, setPages] = useState<any[]>([]);
  const [selectedPage, setSelectedPage] = useState<any>(null);
  const [metaTags, setMetaTags] = useState<any>(null);
  const [keywords, setKeywords] = useState<any[]>([]);
  const [audits, setAudits] = useState<any[]>([]);
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [competitors, setCompetitors] = useState<any[]>([]);
  const [backlinks, setBacklinks] = useState<any[]>([]);
  const [gscData, setGscData] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [showPageModal, setShowPageModal] = useState(false);
  const [showMetaEditor, setShowMetaEditor] = useState(false);
  const [showKeywordModal, setShowKeywordModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showCompetitorModal, setShowCompetitorModal] = useState(false);
  const [view, setView] = useState<'dashboard' | 'pages' | 'keywords' | 'audits' | 'recommendations' | 'templates' | 'competitors' | 'backlinks'>('dashboard');

  const clients = [
    { id: 'f8a6a078-1684-4fc0-a7cb-70699d62105b', name: 'TechCorp' },
    { id: 'be540454-84ee-4c39-9d0f-d14039b03913', name: 'Fashion Brand' }
  ];

  useEffect(() => {
    loadData();
  }, [selectedClient]);

  useEffect(() => {
    if (selectedConnection) {
      loadConnectionData();
    }
  }, [selectedConnection]);

  const loadData = async () => {
    setLoading(true);
    const [connResult, summaryResult, keywordsResult, templatesResult, competitorsResult, backlinksResult] = await Promise.all([
      seoService.getConnections(selectedClient),
      seoService.getClientSummary(selectedClient),
      seoService.getKeywords(selectedClient),
      seoService.getTemplates(),
      seoService.getCompetitors(selectedClient),
      seoService.getBacklinks(selectedClient)
    ]);

    if (connResult.success) {
      setConnections(connResult.data);
      if (connResult.data.length > 0 && !selectedConnection) {
        setSelectedConnection(connResult.data[0]);
      }
    }
    if (summaryResult.success) setSummary(summaryResult.data);
    if (keywordsResult.success) setKeywords(keywordsResult.data);
    if (templatesResult.success) setTemplates(templatesResult.data);
    if (competitorsResult.success) setCompetitors(competitorsResult.data);
    if (backlinksResult.success) setBacklinks(backlinksResult.data);

    setLoading(false);
  };

  const loadConnectionData = async () => {
    if (!selectedConnection) return;

    const [pagesResult, auditsResult, recsResult, gscResult] = await Promise.all([
      seoService.getPages(selectedConnection.id),
      seoService.getAudits(selectedClient, 5),
      seoService.getRecommendations(selectedClient),
      seoService.getGSCData(selectedConnection.id)
    ]);

    if (pagesResult.success) setPages(pagesResult.data);
    if (auditsResult.success) setAudits(auditsResult.data);
    if (recsResult.success) setRecommendations(recsResult.data);
    if (gscResult.success) setGscData(gscResult.data);
  };

  const handleCreateConnection = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);

    const result = await seoService.createConnection({
      client_id: selectedClient,
      website_name: formData.get('website_name') as string,
      website_url: formData.get('website_url') as string,
      ftp_host: formData.get('ftp_host') as string,
      ftp_port: parseInt(formData.get('ftp_port') as string) || 21,
      ftp_username: formData.get('ftp_username') as string,
      ftp_password: formData.get('ftp_password') as string,
      ftp_root_path: formData.get('ftp_root_path') as string || '/',
      is_sftp: formData.get('is_sftp') === 'on',
      created_by_name: 'Current User'
    });

    if (result.success) {
      setShowConnectionModal(false);
      loadData();
    }
  };

  const handleCreatePage = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);

    const result = await seoService.createPage({
      connection_id: selectedConnection.id,
      client_id: selectedClient,
      page_url: formData.get('page_url') as string,
      page_path: formData.get('page_path') as string,
      page_title: formData.get('page_title') as string,
      page_type: formData.get('page_type') as string,
      file_path: formData.get('file_path') as string
    });

    if (result.success) {
      setShowPageModal(false);
      loadConnectionData();
    }
  };

  const handleSaveMetaTags = async () => {
    const result = await seoService.saveMetaTags(metaTags);
    if (result.success) {
      alert('Meta tags saved successfully!');
      loadConnectionData();
    }
  };

  const handleDeployMetaTags = async () => {
    if (!selectedPage || !confirm('Deploy meta tags to live website via FTP?')) return;

    const result = await seoService.deployMetaTags(selectedPage.id, 'Current User');
    if (result.success) {
      alert('Meta tags deployed successfully!');
      loadConnectionData();
      setShowMetaEditor(false);
    }
  };

  const handleRunAudit = async () => {
    if (!selectedConnection) return;

    const result = await seoService.runAudit(selectedConnection.id, selectedPage?.id);
    if (result.success) {
      alert('SEO audit completed!');
      loadConnectionData();
    }
  };

  const handleAddKeyword = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);

    const result = await seoService.addKeyword({
      client_id: selectedClient,
      page_id: selectedPage?.id,
      keyword: formData.get('keyword') as string,
      target_url: formData.get('target_url') as string,
      target_rank: parseInt(formData.get('target_rank') as string) || undefined,
      is_tracking: true
    });

    if (result.success) {
      setShowKeywordModal(false);
      loadData();
    }
  };

  const handleApplyTemplate = async (templateId: string) => {
    if (!selectedPage) return;

    const result = await seoService.applyTemplate(selectedPage.id, templateId, selectedClient);
    if (result.success) {
      alert('Template applied successfully!');
      const metaResult = await seoService.getMetaTags(selectedPage.id);
      if (metaResult.success) {
        setMetaTags(metaResult.data);
      }
      setShowTemplateModal(false);
    }
  };

  const handleAddCompetitor = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);

    const result = await seoService.addCompetitor({
      client_id: selectedClient,
      competitor_name: formData.get('competitor_name') as string,
      competitor_url: formData.get('competitor_url') as string,
      competitor_domain: new URL(formData.get('competitor_url') as string).hostname,
      is_tracking: true
    });

    if (result.success) {
      setShowCompetitorModal(false);
      loadData();
    }
  };

  const loadMetaTags = async (pageId: string) => {
    const result = await seoService.getMetaTags(pageId);
    if (result.success) {
      setMetaTags(result.data || {
        page_id: pageId,
        client_id: selectedClient,
        title: '',
        description: '',
        keywords: [],
        robots: 'index, follow',
        viewport: 'width=device-width, initial-scale=1',
        charset: 'UTF-8'
      });
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getPriorityColor = (priority: string) => {
    if (priority === 'critical') return 'bg-red-100 text-red-800';
    if (priority === 'high') return 'bg-orange-100 text-orange-800';
    if (priority === 'medium') return 'bg-yellow-100 text-yellow-800';
    return 'bg-blue-100 text-blue-800';
  };

  const stats = [
    { label: 'Total Pages', value: summary?.total_pages || 0, icon: FileText, color: 'blue' },
    { label: 'Avg SEO Score', value: summary?.avg_seo_score ? `${summary.avg_seo_score}/100` : 'N/A', icon: TrendingUp, color: 'green' },
    { label: 'Pages with Issues', value: summary?.pages_with_issues || 0, icon: AlertCircle, color: 'red' },
    { label: 'Tracking Keywords', value: summary?.tracking_keywords || 0, icon: Target, color: 'purple' }
  ];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">SEO Manager</h1>
            <p className="text-gray-600 mt-1">Manage meta tags, keywords, and SEO optimization via FTP</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleRunAudit}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <BarChart3 className="h-5 w-5 mr-2" />
              Run Audit
            </button>
            <button
              onClick={() => setShowConnectionModal(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-5 w-5 mr-2" />
              New Connection
            </button>
          </div>
        </div>

        {/* Client & Website Selector */}
        <div className="flex items-center gap-4 mb-6">
          <label className="text-sm font-medium text-gray-700">Client:</label>
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {clients.map(client => (
              <option key={client.id} value={client.id}>{client.name}</option>
            ))}
          </select>

          {connections.length > 0 && (
            <>
              <label className="text-sm font-medium text-gray-700 ml-4">Website:</label>
              <select
                value={selectedConnection?.id || ''}
                onChange={(e) => {
                  const conn = connections.find(c => c.id === e.target.value);
                  setSelectedConnection(conn);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {connections.map(conn => (
                  <option key={conn.id} value={conn.id}>{conn.website_name}</option>
                ))}
              </select>
            </>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                </div>
                <div className={`p-3 bg-${stat.color}-100 rounded-lg`}>
                  <stat.icon className={`h-6 w-6 text-${stat.color}-600`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* View Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg mb-6">
        <div className="flex border-b border-gray-200 overflow-x-auto">
          {[
            { key: 'dashboard', label: 'Dashboard', icon: Globe },
            { key: 'pages', label: `Pages (${pages.length})`, icon: FileText },
            { key: 'recommendations', label: `Recommendations (${recommendations.filter(r => !r.is_completed).length})`, icon: Lightbulb },
            { key: 'templates', label: 'Templates', icon: Layout },
            { key: 'keywords', label: `Keywords (${keywords.length})`, icon: Target },
            { key: 'competitors', label: `Competitors (${competitors.length})`, icon: UsersIcon },
            { key: 'backlinks', label: `Backlinks (${backlinks.length})`, icon: LinkIcon },
            { key: 'audits', label: `Audits (${audits.length})`, icon: BarChart3 }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setView(tab.key as any)}
              className={`px-4 py-3 font-medium whitespace-nowrap ${
                view === tab.key
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <tab.icon className="h-5 w-5 inline mr-2" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Dashboard View */}
          {view === 'dashboard' && (
            <div className="space-y-6">
              {/* GSC Data */}
              {gscData && (
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Google Search Console (Last 30 Days)</h3>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <p className="text-3xl font-bold text-blue-600">{gscData.total_clicks?.toLocaleString() || 0}</p>
                      <p className="text-sm text-gray-600">Clicks</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-blue-600">{gscData.total_impressions?.toLocaleString() || 0}</p>
                      <p className="text-sm text-gray-600">Impressions</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-blue-600">{((gscData.avg_ctr || 0) * 100).toFixed(1)}%</p>
                      <p className="text-sm text-gray-600">CTR</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-blue-600">{(gscData.avg_position || 0).toFixed(1)}</p>
                      <p className="text-sm text-gray-600">Avg Position</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Top Recommendations */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Recommendations</h3>
                {recommendations.filter(r => !r.is_completed).slice(0, 5).map((rec) => (
                  <div key={rec.id} className="border border-gray-200 rounded-lg p-4 mb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(rec.priority)}`}>
                            {rec.priority.toUpperCase()}
                          </span>
                          <span className="text-sm text-gray-600">{rec.category}</span>
                        </div>
                        <h4 className="font-semibold text-gray-900">{rec.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{rec.description}</p>
                        <div className="flex gap-4 mt-2 text-xs text-gray-500">
                          <span>Impact: {rec.impact_score}/10</span>
                          <span>Effort: {rec.effort_score}/10</span>
                        </div>
                      </div>
                      <button
                        onClick={() => seoService.completeRecommendation(rec.id, 'Current User').then(() => loadConnectionData())}
                        className="ml-4 px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                      >
                        Mark Done
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Recent Audits */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Audits</h3>
                {audits.slice(0, 3).map((audit) => (
                  <div key={audit.id} className="border border-gray-200 rounded-lg p-4 mb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`text-2xl font-bold px-4 py-2 rounded ${getScoreColor(audit.overall_score)}`}>
                          {audit.overall_score}/100
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {audit.audit_type === 'full' ? 'Full Site Audit' : 'Page Audit'}
                          </p>
                          <p className="text-sm text-gray-600">
                            {new Date(audit.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <div className="text-center">
                          <p className="text-red-600 font-bold">{audit.critical_issues}</p>
                          <p className="text-gray-600">Critical</p>
                        </div>
                        <div className="text-center">
                          <p className="text-yellow-600 font-bold">{audit.warnings}</p>
                          <p className="text-gray-600">Warnings</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pages View */}
          {view === 'pages' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Website Pages</h3>
                <button
                  onClick={() => setShowPageModal(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Page
                </button>
              </div>

              {pages.length > 0 ? (
                <div className="space-y-3">
                  {pages.map((page) => (
                    <div key={page.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h4 className="font-semibold text-gray-900">{page.page_title || page.page_path}</h4>
                            {page.seo_score && (
                              <span className={`px-2 py-1 rounded text-sm font-medium ${getScoreColor(page.seo_score)}`}>
                                {page.seo_score}/100
                              </span>
                            )}
                            {page.has_issues && <AlertCircle className="h-5 w-5 text-red-600" />}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{page.page_url}</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setSelectedPage(page);
                              loadMetaTags(page.id);
                              setShowMetaEditor(true);
                            }}
                            className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit Meta Tags
                          </button>
                          <button
                            onClick={() => window.open(page.page_url, '_blank')}
                            className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">No pages found. Add your first page to start managing SEO.</p>
                  <button
                    onClick={() => setShowPageModal(true)}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Add Page
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Other views would continue here... simplified for length */}
          {view === 'recommendations' && (
            <div className="text-center py-12">
              <Lightbulb className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Recommendations feature ready - showing {recommendations.length} recommendations</p>
            </div>
          )}

          {view === 'templates' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map((template) => (
                <div key={template.id} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900">{template.name}</h4>
                  <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                  <div className="flex gap-2 mt-3">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">{template.category}</span>
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">{template.industry}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Used {template.usage_count} times</p>
                  <button
                    onClick={() => selectedPage && handleApplyTemplate(template.id)}
                    disabled={!selectedPage}
                    className="mt-3 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    Apply Template
                  </button>
                </div>
              ))}
            </div>
          )}

          {view === 'keywords' && (
            <div>
              <div className="flex justify-between mb-4">
                <h3 className="text-lg font-semibold">Keywords ({keywords.length})</h3>
                <button
                  onClick={() => setShowKeywordModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg"
                >
                  <Plus className="h-4 w-4 inline mr-2" />
                  Add Keyword
                </button>
              </div>
              <div className="text-center py-8 text-gray-600">
                Tracking {keywords.length} keywords
              </div>
            </div>
          )}

          {view === 'competitors' && (
            <div>
              <div className="flex justify-between mb-4">
                <h3 className="text-lg font-semibold">Competitors ({competitors.length})</h3>
                <button
                  onClick={() => setShowCompetitorModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg"
                >
                  <Plus className="h-4 w-4 inline mr-2" />
                  Add Competitor
                </button>
              </div>
              <div className="space-y-3">
                {competitors.map((comp) => (
                  <div key={comp.id} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold">{comp.competitor_name}</h4>
                    <p className="text-sm text-gray-600">{comp.competitor_url}</p>
                    <div className="grid grid-cols-4 gap-4 mt-3">
                      <div>
                        <p className="text-lg font-bold">{comp.domain_authority || 'N/A'}</p>
                        <p className="text-xs text-gray-600">Domain Authority</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold">{comp.organic_keywords || 'N/A'}</p>
                        <p className="text-xs text-gray-600">Keywords</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold">{comp.backlinks_count || 'N/A'}</p>
                        <p className="text-xs text-gray-600">Backlinks</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold">{comp.referring_domains || 'N/A'}</p>
                        <p className="text-xs text-gray-600">Ref Domains</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {view === 'backlinks' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Backlinks ({backlinks.length})</h3>
              <div className="text-center py-8 text-gray-600">
                Monitoring {backlinks.length} backlinks
              </div>
            </div>
          )}

          {view === 'audits' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Audit History ({audits.length})</h3>
              <div className="text-center py-8 text-gray-600">
                {audits.length} audits completed
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals would go here - simplified for brevity */}
      {showConnectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6">
            <h2 className="text-xl font-bold mb-4">Add FTP Connection</h2>
            <form onSubmit={handleCreateConnection}>
              <input
                name="website_name"
                placeholder="Website Name"
                required
                className="w-full px-4 py-2 border rounded-lg mb-3"
              />
              <input
                name="website_url"
                placeholder="https://example.com"
                required
                className="w-full px-4 py-2 border rounded-lg mb-3"
              />
              <input
                name="ftp_host"
                placeholder="ftp.example.com"
                required
                className="w-full px-4 py-2 border rounded-lg mb-3"
              />
              <input
                name="ftp_username"
                placeholder="FTP Username"
                required
                className="w-full px-4 py-2 border rounded-lg mb-3"
              />
              <input
                name="ftp_password"
                type="password"
                placeholder="FTP Password"
                required
                className="w-full px-4 py-2 border rounded-lg mb-3"
              />
              <input
                name="ftp_port"
                type="number"
                defaultValue="21"
                className="w-full px-4 py-2 border rounded-lg mb-3"
              />
              <input
                name="ftp_root_path"
                placeholder="/public_html"
                defaultValue="/"
                className="w-full px-4 py-2 border rounded-lg mb-3"
              />
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowConnectionModal(false)}
                  className="px-4 py-2 border rounded-lg"
                >
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg">
                  Connect
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <h2 className="text-xl font-bold mb-4">Add Page</h2>
            <form onSubmit={handleCreatePage}>
              <input name="page_url" placeholder="Page URL" required className="w-full px-4 py-2 border rounded-lg mb-3" />
              <input name="page_path" placeholder="/page" required className="w-full px-4 py-2 border rounded-lg mb-3" />
              <input name="page_title" placeholder="Page Title" className="w-full px-4 py-2 border rounded-lg mb-3" />
              <select name="page_type" className="w-full px-4 py-2 border rounded-lg mb-3">
                <option value="page">Page</option>
                <option value="home">Home</option>
                <option value="article">Article</option>
                <option value="product">Product</option>
              </select>
              <input name="file_path" placeholder="/public_html/page.html" className="w-full px-4 py-2 border rounded-lg mb-3" />
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowPageModal(false)} className="px-4 py-2 border rounded-lg">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg">Add Page</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
